import React, { useState, useEffect } from "react";
import { Search, ChevronDown, ChevronRight, Info } from "lucide-react";
import axios from "axios";
import { useTranslation } from "react-i18next";

interface WZCode {
  code: string;
  full_code: string;
  level: number;
  section_code: string;
  section_description_de: string;
  section_description_en: string;
  division_code: string;
  division_description_de: string;
  division_description_en: string;
  group_code: string;
  group_description_de: string;
  group_description_en: string;
  class_code: string;
  class_description_de: string;
  class_description_en: string;
  description_de: string;
  description_en: string;
  keywords_de?: string[];
  keywords_en?: string[];
}

interface ApiResponse {
  wz_codes: WZCode[];
  count: number;
}

interface WZSubclass {
  code: string;
  full_code: string;
  description_de: string;
  description_en: string;
}

interface WZClassNode {
  code: string;
  description_de: string;
  description_en: string;
  subclasses: WZSubclass[];
}

interface WZGroupNode {
  code: string;
  description_de: string;
  description_en: string;
  classes: Record<string, WZClassNode>;
}

interface WZDivisionNode {
  code: string;
  description_de: string;
  description_en: string;
  groups: Record<string, WZGroupNode>;
}

interface WZSectionNode {
  code: string;
  description_de: string;
  description_en: string;
  divisions: Record<string, WZDivisionNode>;
}

type WZHierarchy = Record<string, WZSectionNode>;

interface WZ2025ViewerProps {
  highlightCode?: string | null;
}

const WZ2025Viewer: React.FC<WZ2025ViewerProps> = ({ highlightCode }) => {
  const { t, i18n } = useTranslation("website-classifications");
  const interfaceLanguage = (i18n.resolvedLanguage ?? i18n.language).split(
    "-",
  )[0];
  const taxonomyLanguage: "de" | "en" =
    interfaceLanguage === "de" ? "de" : "en";
  const isSwedish = interfaceLanguage === "sv";
  const [data, setData] = useState<WZCode[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedItem, setSelectedItem] = useState<WZCode | null>(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const fetchWZCodes = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get<ApiResponse>(
          `https://hub.webmetic.de/classifications/wz2025/all`,
        );
        setData(response.data.wz_codes);

        // Auto-expand first section
        if (response.data.wz_codes.length > 0) {
          setExpanded({ [response.data.wz_codes[0].section_code]: true });
        }
        setIsLoading(false);
      } catch (err) {
        console.error("Error fetching WZ 2025 codes:", err);
        setHasError(true);
        setIsLoading(false);
      }
    };
    fetchWZCodes();
  }, []);

  // Handle external highlight code (from hero search)
  useEffect(() => {
    if (!highlightCode || data.length === 0) return;
    const item = data.find((d) => d.code === highlightCode);
    if (!item) return;

    // Expand all hierarchy levels
    setExpanded((prev) => ({
      ...prev,
      [item.section_code]: true,
      [item.division_code]: true,
      [item.group_code]: true,
      [item.class_code]: true,
    }));
    setSelectedItem(item);
    setSearchTerm("");

    // Scroll to the element after DOM updates
    requestAnimationFrame(() => {
      const el = document.getElementById(`wz-code-${highlightCode}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    });
  }, [highlightCode, data]);

  // Build 5-level hierarchy: Section > Division > Group > Class > Subclass
  const organizeData = () => {
    const sections: WZHierarchy = {};

    data.forEach((item) => {
      // Section
      if (!sections[item.section_code]) {
        sections[item.section_code] = {
          code: item.section_code,
          description_de: item.section_description_de,
          description_en: item.section_description_en,
          divisions: {},
        };
      }
      const section = sections[item.section_code];

      // Division
      if (!section.divisions[item.division_code]) {
        section.divisions[item.division_code] = {
          code: item.division_code,
          description_de: item.division_description_de,
          description_en: item.division_description_en,
          groups: {},
        };
      }
      const division = section.divisions[item.division_code];

      // Group
      if (!division.groups[item.group_code]) {
        division.groups[item.group_code] = {
          code: item.group_code,
          description_de: item.group_description_de,
          description_en: item.group_description_en,
          classes: {},
        };
      }
      const group = division.groups[item.group_code];

      // Class
      if (!group.classes[item.class_code]) {
        group.classes[item.class_code] = {
          code: item.class_code,
          description_de: item.class_description_de,
          description_en: item.class_description_en,
          subclasses: [],
        };
      }

      // Subclass (the actual item)
      group.classes[item.class_code].subclasses.push({
        code: item.code,
        full_code: item.full_code,
        description_de: item.description_de,
        description_en: item.description_en,
      });
    });

    return sections;
  };

  const organizedData = organizeData();

  // Filter
  const filteredData = () => {
    if (!searchTerm.trim()) return organizedData;
    const s = searchTerm.toLowerCase();
    const filtered: WZHierarchy = {};

    const matching = data.filter(
      (item) =>
        item.code.toLowerCase().includes(s) ||
        item[`description_${taxonomyLanguage}`].toLowerCase().includes(s) ||
        item[`section_description_${taxonomyLanguage}`]
          .toLowerCase()
          .includes(s) ||
        item[`division_description_${taxonomyLanguage}`]
          .toLowerCase()
          .includes(s) ||
        item[`group_description_${taxonomyLanguage}`]
          .toLowerCase()
          .includes(s) ||
        item[`class_description_${taxonomyLanguage}`]
          .toLowerCase()
          .includes(s) ||
        (item[`keywords_${taxonomyLanguage}`] ?? []).some((k) =>
          k.toLowerCase().includes(s),
        ),
    );

    matching.forEach((item) => {
      if (!filtered[item.section_code]) {
        filtered[item.section_code] = {
          code: item.section_code,
          description_de: item.section_description_de,
          description_en: item.section_description_en,
          divisions: {},
        };
      }
      const sec = filtered[item.section_code];

      if (!sec.divisions[item.division_code]) {
        sec.divisions[item.division_code] = {
          code: item.division_code,
          description_de: item.division_description_de,
          description_en: item.division_description_en,
          groups: {},
        };
      }
      const div = sec.divisions[item.division_code];

      if (!div.groups[item.group_code]) {
        div.groups[item.group_code] = {
          code: item.group_code,
          description_de: item.group_description_de,
          description_en: item.group_description_en,
          classes: {},
        };
      }
      const grp = div.groups[item.group_code];

      if (!grp.classes[item.class_code]) {
        grp.classes[item.class_code] = {
          code: item.class_code,
          description_de: item.class_description_de,
          description_en: item.class_description_en,
          subclasses: [],
        };
      }

      grp.classes[item.class_code].subclasses.push({
        code: item.code,
        full_code: item.full_code,
        description_de: item.description_de,
        description_en: item.description_en,
      });
    });

    return filtered;
  };

  const displayData = filteredData();

  const toggle = (key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTerm = e.target.value;
    setSearchTerm(newTerm);

    if (newTerm.trim() && newTerm.length > searchTerm.length) {
      const s = newTerm.toLowerCase();
      const newExpanded: Record<string, boolean> = {};

      data
        .filter(
          (item) =>
            item.code.toLowerCase().includes(s) ||
            item[`description_${taxonomyLanguage}`].toLowerCase().includes(s) ||
            item[`section_description_${taxonomyLanguage}`]
              .toLowerCase()
              .includes(s) ||
            item[`division_description_${taxonomyLanguage}`]
              .toLowerCase()
              .includes(s) ||
            item[`group_description_${taxonomyLanguage}`]
              .toLowerCase()
              .includes(s) ||
            item[`class_description_${taxonomyLanguage}`]
              .toLowerCase()
              .includes(s) ||
            (item[`keywords_${taxonomyLanguage}`] ?? []).some((k) =>
              k.toLowerCase().includes(s),
            ),
        )
        .forEach((item) => {
          newExpanded[item.section_code] = true;
          newExpanded[item.division_code] = true;
          newExpanded[item.group_code] = true;
          newExpanded[item.class_code] = true;
        });

      setExpanded((prev) => ({ ...prev, ...newExpanded }));
    }

    if (!newTerm.trim() && searchTerm.trim()) {
      const initial: Record<string, boolean> = {};
      if (data.length > 0) initial[data[0].section_code] = true;
      setExpanded(initial);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-energetic-mint"></div>
        <span className="ml-3">{t("viewer.wz.loading")}</span>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center text-red-600">
        <p>{t("viewer.wz.error")}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 rounded bg-energetic-mint-400 px-4 py-2 hover:bg-energetic-mint-500"
        >
          {t("viewer.retry")}
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto flex flex-col gap-4 p-4 lg:flex-row">
      {/* Left side: Tree navigation */}
      <div className="w-full lg:w-3/5">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold dark:text-white">
            {t("viewer.overview")}
          </h2>
          {isSwedish && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t("viewer.swedishTaxonomyFallback")}
            </p>
          )}
        </div>

        <div className="relative mb-4">
          <input
            type="text"
            placeholder={t("viewer.searchPlaceholder")}
            aria-label={t("viewer.searchAria")}
            className="w-full rounded border p-2 pl-10 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
            value={searchTerm}
            onChange={handleSearch}
          />
          <Search className="absolute left-3 top-3 text-gray-400" size={16} />
        </div>

        <div className="max-h-[75vh] overflow-y-auto rounded border bg-white p-2 dark:border-gray-700 dark:bg-gray-800">
          {data.length === 0 ? (
            <div className="flex rounded-lg bg-amber-50 p-4 text-amber-800 dark:bg-amber-900 dark:text-amber-100">
              <p>{t("viewer.wz.unavailable")}</p>
            </div>
          ) : Object.keys(displayData).length > 0 ? (
            Object.keys(displayData)
              .sort()
              .map((sectionKey) => {
                const section = displayData[sectionKey];
                return (
                  <div key={sectionKey} className="mb-2">
                    {/* Section */}
                    <div
                      className="flex cursor-pointer items-center rounded-md p-2 transition-colors hover:bg-platin dark:bg-gray-700 dark:hover:bg-gray-600"
                      onClick={() => toggle(sectionKey)}
                    >
                      {expanded[sectionKey] ? (
                        <ChevronDown
                          size={16}
                          className="mr-1 dark:text-gray-300"
                        />
                      ) : (
                        <ChevronRight
                          size={16}
                          className="mr-1 dark:text-gray-300"
                        />
                      )}
                      <span className="mr-2 font-semibold dark:text-white">
                        {section.code}
                      </span>
                      <span className="dark:text-gray-300">
                        {section[`description_${taxonomyLanguage}`]}
                      </span>
                    </div>

                    {expanded[sectionKey] && (
                      <div className="ml-5 mt-1">
                        {Object.keys(section.divisions)
                          .sort()
                          .map((divKey) => {
                            const division = section.divisions[divKey];
                            return (
                              <div key={divKey} className="mb-1">
                                {/* Division */}
                                <div
                                  className="flex cursor-pointer items-center rounded p-1.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                                  onClick={() => toggle(divKey)}
                                >
                                  {expanded[divKey] ? (
                                    <ChevronDown
                                      size={14}
                                      className="mr-1 dark:text-gray-400"
                                    />
                                  ) : (
                                    <ChevronRight
                                      size={14}
                                      className="mr-1 dark:text-gray-400"
                                    />
                                  )}
                                  <span className="mr-2 font-medium dark:text-gray-200">
                                    {division.code}
                                  </span>
                                  <span className="text-sm dark:text-gray-300">
                                    {
                                      division[
                                        `description_${taxonomyLanguage}`
                                      ]
                                    }
                                  </span>
                                </div>

                                {expanded[divKey] && (
                                  <div className="ml-5">
                                    {Object.keys(division.groups)
                                      .sort()
                                      .map((grpKey) => {
                                        const group = division.groups[grpKey];
                                        return (
                                          <div key={grpKey} className="mb-1">
                                            {/* Group */}
                                            <div
                                              className="flex cursor-pointer items-center rounded p-1.5 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                                              onClick={() => toggle(grpKey)}
                                            >
                                              {expanded[grpKey] ? (
                                                <ChevronDown
                                                  size={13}
                                                  className="mr-1 dark:text-gray-400"
                                                />
                                              ) : (
                                                <ChevronRight
                                                  size={13}
                                                  className="mr-1 dark:text-gray-400"
                                                />
                                              )}
                                              <span className="mr-2 text-sm font-medium dark:text-gray-200">
                                                {group.code}
                                              </span>
                                              <span className="text-sm dark:text-gray-300">
                                                {
                                                  group[
                                                    `description_${taxonomyLanguage}`
                                                  ]
                                                }
                                              </span>
                                            </div>

                                            {expanded[grpKey] && (
                                              <div className="ml-5">
                                                {Object.keys(group.classes)
                                                  .sort()
                                                  .map((clsKey) => {
                                                    const cls =
                                                      group.classes[clsKey];
                                                    return (
                                                      <div
                                                        key={clsKey}
                                                        className="mb-1"
                                                      >
                                                        {/* Class */}
                                                        <div
                                                          className="flex cursor-pointer items-center rounded p-1 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
                                                          onClick={() =>
                                                            toggle(clsKey)
                                                          }
                                                        >
                                                          {expanded[clsKey] ? (
                                                            <ChevronDown
                                                              size={12}
                                                              className="mr-1 dark:text-gray-400"
                                                            />
                                                          ) : (
                                                            <ChevronRight
                                                              size={12}
                                                              className="mr-1 dark:text-gray-400"
                                                            />
                                                          )}
                                                          <span className="mr-2 text-sm font-medium dark:text-gray-200">
                                                            {cls.code}
                                                          </span>
                                                          <span className="text-sm dark:text-gray-300">
                                                            {
                                                              cls[
                                                                `description_${taxonomyLanguage}`
                                                              ]
                                                            }
                                                          </span>
                                                        </div>

                                                        {expanded[clsKey] && (
                                                          <div className="ml-5">
                                                            {cls.subclasses
                                                              .sort((a, b) =>
                                                                a.code.localeCompare(
                                                                  b.code,
                                                                ),
                                                              )
                                                              .map((sub) => {
                                                                const completeItem =
                                                                  data.find(
                                                                    (d) =>
                                                                      d.code ===
                                                                      sub.code,
                                                                  );
                                                                return (
                                                                  <div
                                                                    id={`wz-code-${sub.code}`}
                                                                    key={
                                                                      sub.code
                                                                    }
                                                                    className={`flex cursor-pointer items-center rounded p-1.5 text-sm transition-colors hover:bg-energetic-mint-500 dark:hover:bg-gray-700 ${
                                                                      selectedItem?.code ===
                                                                      sub.code
                                                                        ? "bg-energetic-mint-500 dark:bg-gray-600 dark:text-white"
                                                                        : "dark:text-gray-300"
                                                                    }`}
                                                                    onClick={() =>
                                                                      setSelectedItem(
                                                                        completeItem ||
                                                                          null,
                                                                      )
                                                                    }
                                                                  >
                                                                    <span className="font-mono mr-2 w-16 font-semibold">
                                                                      {sub.code}
                                                                    </span>
                                                                    <span>
                                                                      {
                                                                        sub[
                                                                          `description_${taxonomyLanguage}`
                                                                        ]
                                                                      }
                                                                    </span>
                                                                  </div>
                                                                );
                                                              })}
                                                          </div>
                                                        )}
                                                      </div>
                                                    );
                                                  })}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                );
              })
          ) : (
            <div className="p-4 text-center text-gray-500 dark:text-gray-400">
              {t("viewer.noResultsFor")}
              <span className="font-semibold">{searchTerm}</span>
            </div>
          )}
        </div>
      </div>

      {/* Right side: Details */}
      <div className="w-full rounded border bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 lg:w-2/5">
        {selectedItem ? (
          <div>
            <div className="mb-2 flex items-start justify-between">
              <h3 className="text-lg font-bold text-pulse dark:text-blue-400">
                {selectedItem.full_code}
              </h3>
            </div>
            <p className="mb-4 text-lg font-medium dark:text-gray-200">
              {selectedItem[`description_${taxonomyLanguage}`]}
            </p>

            <div className="mb-4 rounded-lg bg-platin p-4 shadow-sm dark:bg-gray-700">
              <h4 className="mb-3 flex items-center font-medium dark:text-gray-200">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mr-2"
                >
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <polyline points="19 12 12 19 5 12"></polyline>
                </svg>
                {t("viewer.hierarchy")}
              </h4>
              <div className="ml-2">
                {/* Section */}
                <div className="mb-3 border-b border-stone pb-3 dark:border-gray-600">
                  <div className="mb-1 text-xs font-medium text-carbon dark:text-gray-400">
                    {t("viewer.levels.section")}
                  </div>
                  <span className="mr-2 inline-block rounded bg-stone px-1.5 py-0.5 font-semibold text-pulse dark:bg-gray-700 dark:text-blue-300">
                    {selectedItem.section_code}
                  </span>
                  <span className="dark:text-gray-200">
                    {selectedItem[`section_description_${taxonomyLanguage}`]}
                  </span>
                </div>
                {/* Division */}
                <div className="mb-3 border-b border-stone pb-3 dark:border-gray-600">
                  <div className="mb-1 text-xs font-medium text-carbon dark:text-gray-400">
                    {t("viewer.levels.division")}
                  </div>
                  <span className="mr-2 inline-block rounded bg-stone px-1.5 py-0.5 font-semibold text-pulse dark:bg-gray-700 dark:text-blue-300">
                    {selectedItem.division_code}
                  </span>
                  <span className="dark:text-gray-200">
                    {selectedItem[`division_description_${taxonomyLanguage}`]}
                  </span>
                </div>
                {/* Group */}
                <div className="mb-3 border-b border-stone pb-3 dark:border-gray-600">
                  <div className="mb-1 text-xs font-medium text-carbon dark:text-gray-400">
                    {t("viewer.levels.group")}
                  </div>
                  <span className="mr-2 inline-block rounded bg-stone px-1.5 py-0.5 font-semibold text-pulse dark:bg-gray-700 dark:text-blue-300">
                    {selectedItem.group_code}
                  </span>
                  <span className="dark:text-gray-200">
                    {selectedItem[`group_description_${taxonomyLanguage}`]}
                  </span>
                </div>
                {/* Class */}
                <div className="mb-3 border-b border-stone pb-3 dark:border-gray-600">
                  <div className="mb-1 text-xs font-medium text-carbon dark:text-gray-400">
                    {t("viewer.levels.class")}
                  </div>
                  <span className="mr-2 inline-block rounded bg-stone px-1.5 py-0.5 font-semibold text-pulse dark:bg-gray-700 dark:text-blue-300">
                    {selectedItem.class_code}
                  </span>
                  <span className="dark:text-gray-200">
                    {selectedItem[`class_description_${taxonomyLanguage}`]}
                  </span>
                </div>
                {/* Subclass */}
                <div>
                  <div className="mb-1 text-xs font-medium text-carbon dark:text-gray-400">
                    {t("viewer.levels.subclass")}
                  </div>
                  <span className="mr-2 inline-block rounded bg-stone px-1.5 py-0.5 font-semibold text-pulse dark:bg-gray-700 dark:text-blue-300">
                    {selectedItem.code}
                  </span>
                  <span className="dark:text-gray-200">
                    {selectedItem[`description_${taxonomyLanguage}`]}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center rounded-lg bg-platin p-3 text-sm dark:bg-gray-600 dark:text-gray-300">
              <Info
                size={16}
                className="mr-2 shrink-0 text-pulse dark:text-blue-300"
              />
              <span>{t("viewer.wz.info")}</span>
            </div>
          </div>
        ) : (
          <div className="flex h-full items-center justify-center p-8 dark:text-gray-400">
            <div className="text-center">
              <div className="mb-3 inline-block rounded-full bg-platin p-3 dark:bg-gray-700">
                <Info size={32} className="text-pulse dark:text-gray-500" />
              </div>
              <p className="mb-2 text-lg font-medium dark:text-gray-300">
                {t("viewer.wz.detailsTitle")}
              </p>
              <p className="text-sm dark:text-gray-400">
                {t("viewer.wz.detailsPrompt")}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WZ2025Viewer;
